import React, { useState, useEffect, useRef } from 'react';
import { receptionsApi, purchaseOrdersApi, productsApi, depotsApi, locationsApi } from '../lib/api';
import api from '../lib/api';
import LabelPrintDialog from '../components/LabelPrintDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../lib/errorUtils';
import { 
  Plus, Truck, Package, ClipboardCheck, Calendar,
  CheckCircle, Clock, X, FileText, Eye, AlertTriangle,
  Upload, Loader2, Check, XCircle, Scan, Printer, Trash2, Search
} from 'lucide-react';

const ReceptionsPage = () => {
  const [receptions, setReceptions] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('receptions');
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [showReceptionDialog, setShowReceptionDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [recSearch, setRecSearch] = useState('');
  const [recSortBy, setRecSortBy] = useState('received_at');
  const [recSortOrder, setRecSortOrder] = useState('desc');

  // PO import state
  const [showPOImportDialog, setShowPOImportDialog] = useState(false);
  const [poImporting, setPOImporting] = useState(false);
  const [poParsed, setPOParsed] = useState(null);
  const poFileInputRef = useRef(null);
  const [poDragOver, setPODragOver] = useState(false);
  const poDropCallbackRef = useRef(null);
  const [poCreatingProduct, setPOCreatingProduct] = useState(null);
  const [poAssociating, setPOAssociating] = useState(null);
  const [poAssociateSearch, setPOAssociateSearch] = useState('');

  const setPODropZoneRef = (el) => {
    if (poDropCallbackRef.current && poDropCallbackRef.current !== el) {
      const prev = poDropCallbackRef.current;
      prev.removeEventListener('dragover', prev._onDragOver, true);
      prev.removeEventListener('dragleave', prev._onDragLeave, true);
      prev.removeEventListener('drop', prev._onDrop, true);
    }
    poDropCallbackRef.current = el;
    if (!el) return;
    el._onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setPODragOver(true); };
    el._onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setPODragOver(false); };
    el._onDrop = (e) => {
      e.preventDefault(); e.stopPropagation(); setPODragOver(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) handlePOFileUpload(file);
    };
    el.addEventListener('dragover', el._onDragOver, true);
    el.addEventListener('dragleave', el._onDragLeave, true);
    el.addEventListener('drop', el._onDrop, true);
  };

  const [orderForm, setOrderForm] = useState({
    supplier: '',
    order_date: new Date().toISOString().split('T')[0],
    expected_date: '',
    notes: '',
    items: [],
  });

  const [receptionForm, setReceptionForm] = useState({
    order_id: '',
    depot_id: '',
    notes: '',
    received_items: [],
  });

  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedQuantity, setSelectedQuantity] = useState('');
  const [selectedUnitPrice, setSelectedUnitPrice] = useState('');

  // Delivery note upload state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [parsedDelivery, setParsedDelivery] = useState(null);
  const [uploadDepotId, setUploadDepotId] = useState('');
  // receptionDepots: { [product_id]: [ {depot_id, depot_name, qty} ] }
  const [receptionDepots, setReceptionDepots] = useState({});
  const [validating, setValidating] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const dropListenersRef = useRef(null);
  const [showCreateFromUnmatched, setShowCreateFromUnmatched] = useState(null);
  const [newProductForm, setNewProductForm] = useState({});
  const fileInputRef = useRef(null);
  const [showLabelDialog, setShowLabelDialog] = useState(false);
  const [labelItems, setLabelItems] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [receptionsRes, ordersRes, productsRes, depotsRes] = await Promise.all([
        receptionsApi.getAll(),
        purchaseOrdersApi.getAll(),
        productsApi.getAll({ limit: 500 }),
        depotsApi.getAll(),
      ]);

      setReceptions(receptionsRes.data);
      setPurchaseOrders(ordersRes.data);
      setProducts(productsRes.data.items || []);
      setDepots(depotsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReception = async (receptionId) => {
    if (!window.confirm('Supprimer cette réception ? Cette action est irréversible.')) return;
    try {
      await receptionsApi.delete(receptionId);
      toast.success('Réception supprimée');
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la suppression'));
    }
  };

  const processFile = async (file) => {
    if (!file) return;

    const maxSize = 20 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('Fichier trop volumineux (max 20 Mo)');
      return;
    }

    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    const validExts = ['.pdf', '.jpg', '.jpeg', '.png', '.webp'];
    const ext = file.name?.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!validTypes.includes(file.type) && !validExts.includes(ext)) {
      toast.error('Format non supporté. Utilisez PDF, JPG, PNG ou WEBP.');
      return;
    }

    setUploading(true);
    setParsedDelivery(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/receptions/parse-delivery-note', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });

      setParsedDelivery(response.data);
      toast.success(`${response.data.total_matched} produits reconnus, ${response.data.total_unmatched} non reconnus`);
    } catch (error) {
      const msg = getApiErrorMessage(error, 'Erreur lors de l\'analyse du document');
      toast.error(msg);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleFileUpload = (event) => {
    processFile(event.target.files?.[0]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  // Callback ref: attach native DOM listeners when element mounts, remove on unmount
  const dropZoneCallbackRef = useRef(null);
  const setDropZoneRef = (el) => {
    // Cleanup previous
    if (dropZoneCallbackRef.current && dropZoneCallbackRef.current !== el) {
      const prev = dropZoneCallbackRef.current;
      prev.removeEventListener('dragover', prev._onDragOver, true);
      prev.removeEventListener('dragleave', prev._onDragLeave, true);
      prev.removeEventListener('drop', prev._onDrop, true);
    }
    dropZoneCallbackRef.current = el;
    if (!el) return;
    el._onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
    el._onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };
    el._onDrop = (e) => {
      e.preventDefault(); e.stopPropagation(); setDragOver(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) processFile(file);
    };
    el.addEventListener('dragover', el._onDragOver, true);
    el.addEventListener('dragleave', el._onDragLeave, true);
    el.addEventListener('drop', el._onDrop, true);
  };

  const handleUpdateParsedQty = (index, newQty) => {
    if (!parsedDelivery) return;
    const items = [...parsedDelivery.matched_items];
    items[index] = { ...items[index], quantity: parseInt(newQty) || 0 };
    setParsedDelivery({ ...parsedDelivery, matched_items: items });
  };

  const handleRemoveParsedItem = (index) => {
    if (!parsedDelivery) return;
    const items = parsedDelivery.matched_items.filter((_, i) => i !== index);
    setParsedDelivery({ ...parsedDelivery, matched_items: items, total_matched: items.length });
  };

  const handleOpenCreateFromUnmatched = (unmatchedItem, index) => {
    setNewProductForm({
      reference: unmatchedItem.reference || '',
      designation: unmatchedItem.designation || '',
      category: unmatchedItem.category || '',
      risk_class: unmatchedItem.risk_class || '',
      un_code: unmatchedItem.un_code || '',
      // NEC + poids bruts arrivent en kg, convertis en grammes pour l'affichage
      nec: unmatchedItem.nec != null ? String(Math.round(unmatchedItem.nec * 1000 * 100) / 100) : '',
      gross_weight: unmatchedItem.gross_weight != null ? String(Math.round(unmatchedItem.gross_weight * 1000 * 100) / 100) : '',
      caliber: unmatchedItem.caliber || '',
      manufacturer: unmatchedItem.manufacturer || '',
      homologation_number: unmatchedItem.homologation_number || '',
      quantity: unmatchedItem.quantity || 1,
      unit_type: unmatchedItem.unit_type || 'unites',
      _unmatched_index: index,
    });
    setShowCreateFromUnmatched(true);
  };

  const handleCreateAndAddProduct = async () => {
    if (!newProductForm.reference || !newProductForm.designation) {
      toast.error('Référence et désignation obligatoires');
      return;
    }
    try {
      const payload = {
        reference: newProductForm.reference,
        designation: newProductForm.designation,
        category: newProductForm.category || 'F4',
      };
      if (newProductForm.risk_class) payload.risk_class = newProductForm.risk_class;
      if (newProductForm.un_code) payload.un_code = newProductForm.un_code;
      // NEC saisi en grammes, converti en kg pour le stockage
      if (newProductForm.nec) payload.nec = parseFloat(newProductForm.nec) / 1000;
      if (newProductForm.caliber) payload.caliber = newProductForm.caliber;
      if (newProductForm.manufacturer) payload.manufacturer = newProductForm.manufacturer;
      if (newProductForm.homologation_number) payload.homologation_number = newProductForm.homologation_number;
      // Poids brut saisi en grammes, converti en kg pour le stockage
      if (newProductForm.gross_weight) payload.gross_weight = parseFloat(newProductForm.gross_weight) / 1000;

      const res = await productsApi.create(payload);
      const createdProduct = res.data;

      // Move from unmatched to matched
      const unmatchedIndex = newProductForm._unmatched_index;
      const newMatched = [...parsedDelivery.matched_items, {
        product_id: createdProduct.id,
        reference: createdProduct.reference,
        designation: createdProduct.designation,
        quantity: newProductForm.quantity,
        original_quantity: newProductForm.quantity,
        unit_type: newProductForm.unit_type,
        risk_class: createdProduct.risk_class,
        matched: true,
      }];
      const newUnmatched = parsedDelivery.unmatched_items.filter((_, i) => i !== unmatchedIndex);

      setParsedDelivery({
        ...parsedDelivery,
        matched_items: newMatched,
        unmatched_items: newUnmatched,
        total_matched: newMatched.length,
        total_unmatched: newUnmatched.length,
      });

      setShowCreateFromUnmatched(false);
      setNewProductForm({});
      toast.success(`Produit ${createdProduct.reference} créé et ajouté à la liste`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la création du produit'));
    }
  };

  const handleValidateDelivery = async () => {
    if (!parsedDelivery) return;

    const validItems = parsedDelivery.matched_items.filter(i => i.quantity > 0);
    if (validItems.length === 0) {
      toast.error('Aucun produit à réceptionner');
      return;
    }

    // Check all items have at least one depot assigned
    const unassigned = validItems.filter(i => {
      const allocs = receptionDepots[i.product_id] || [];
      return allocs.reduce((s, a) => s + (a.qty || 0), 0) === 0;
    });
    if (unassigned.length > 0) {
      toast.error(`${unassigned.length} produit(s) sans dépôt assigné. Renseignez la répartition.`);
      return;
    }

    setValidating(true);
    try {
      // Flatten: one received_item per (product, depot) pair
      const flatItems = [];
      for (const item of validItems) {
        const allocs = receptionDepots[item.product_id] || [];
        for (const alloc of allocs) {
          if (alloc.qty > 0) {
            flatItems.push({
              product_id: item.product_id,
              quantity: alloc.qty,
              depot_id: alloc.depot_id,
              lot_number: `BL-${parsedDelivery.delivery_number || new Date().toISOString().slice(0, 10)}`,
            });
          }
        }
      }

      // Main depot = most used
      const depotCounts = {};
      flatItems.forEach(i => { depotCounts[i.depot_id] = (depotCounts[i.depot_id] || 0) + i.quantity; });
      const mainDepotId = Object.entries(depotCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || null;

      await receptionsApi.create({
        depot_id: mainDepotId,
        notes: `Import BL ${parsedDelivery.delivery_number || ''} - ${parsedDelivery.supplier || ''}`.trim(),
        received_items: flatItems,
      });
      toast.success(`${validItems.length} produits réceptionnés dans ${Object.keys(depotCounts).length} dépôt(s)`);
      setShowUploadDialog(false);
      setParsedDelivery(null);
      setUploadDepotId('');
      setReceptionDepots({});
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la réception'));
    } finally {
      setValidating(false);
    }
  };

  const handleReceptionDepotQty = (productId, depotId, depotName, newQty) => {
    setReceptionDepots(prev => {
      const current = prev[productId] || [];
      const existing = current.find(s => s.depot_id === depotId);
      let updated;
      if (newQty <= 0) {
        updated = current.filter(s => s.depot_id !== depotId);
      } else if (existing) {
        updated = current.map(s => s.depot_id === depotId ? { ...s, qty: newQty } : s);
      } else {
        updated = [...current, { depot_id: depotId, depot_name: depotName, qty: newQty }];
      }
      return { ...prev, [productId]: updated };
    });
  };

  const getReceptionAllocated = (productId) => {
    return (receptionDepots[productId] || []).reduce((s, a) => s + (a.qty || 0), 0);
  };

  // Quick assign all items to one depot
  const handleAssignAllToDepot = (depotId) => {
    if (!parsedDelivery || !depotId) return;
    const depotName = depots.find(d => d.id === depotId)?.name || depotId;
    const newAllocs = {};
    for (const item of parsedDelivery.matched_items) {
      if (item.quantity > 0) {
        newAllocs[item.product_id] = [{ depot_id: depotId, depot_name: depotName, qty: item.quantity }];
      }
    }
    setReceptionDepots(newAllocs);
  };


  const handleAddOrderItem = () => {
    if (!selectedProduct || !selectedQuantity) {
      toast.error('Sélectionnez un produit et une quantité');
      return;
    }

    const product = products.find(p => p.id === selectedProduct);
    if (!product) return;

    setOrderForm({
      ...orderForm,
      items: [...orderForm.items, {
        product_id: selectedProduct,
        product_reference: product.reference,
        product_designation: product.designation,
        quantity: parseInt(selectedQuantity),
        unit_price: selectedUnitPrice ? parseFloat(selectedUnitPrice) : 0,
      }]
    });

    setSelectedProduct('');
    setSelectedQuantity('');
    setSelectedUnitPrice('');
  };

  const handleRemoveOrderItem = (index) => {
    setOrderForm({
      ...orderForm,
      items: orderForm.items.filter((_, i) => i !== index)
    });
  };

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    
    if (orderForm.items.length === 0) {
      toast.error('Ajoutez au moins un produit');
      return;
    }

    try {
      const data = {
        supplier: orderForm.supplier,
        order_date: new Date(orderForm.order_date).toISOString(),
        expected_date: orderForm.expected_date ? new Date(orderForm.expected_date).toISOString() : null,
        notes: orderForm.notes || null,
        items: orderForm.items.map(i => ({
          product_id: i.product_id,
          quantity: i.quantity,
          unit_price: i.unit_price
        })),
      };

      await purchaseOrdersApi.create(data);
      toast.success('Bon de commande créé');
      setShowOrderDialog(false);
      fetchData();
      setOrderForm({
        supplier: '',
        order_date: new Date().toISOString().split('T')[0],
        expected_date: '',
        notes: '',
        items: [],
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la création'));
    }
  };

  const handleStartReception = (order) => {
    setSelectedOrder(order);
    setReceptionForm({
      order_id: order.id,
      depot_id: '',
      notes: '',
      received_items: order.items.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        lot_number: '',
        boxes: 1,
        location_id: '',
      })),
    });
    setShowReceptionDialog(true);
  };

  // PO Import handlers
  const handlePOFileUpload = async (file) => {
    if (!file) return;
    setPOImporting(true);
    setPOParsed(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/receptions/parse-purchase-order', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      setPOParsed(res.data);
      toast.success(`${res.data.total_matched} produit(s) reconnu(s)`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Erreur lors de l'analyse"));
    }
    setPOImporting(false);
  };

  const handleCreateOrderFromPO = async () => {
    if (!poParsed || poParsed.matched_items.length === 0) return;
    try {
      const items = poParsed.matched_items.map(i => ({
        product_id: i.product_id,
        quantity: i.quantity,
        unit_price: i.unit_price || 0,
      }));

      // Parse dates
      let orderDate = new Date().toISOString();
      if (poParsed.order_date) {
        const parts = poParsed.order_date.split('/');
        if (parts.length === 3) orderDate = new Date(parts[2], parts[1] - 1, parts[0]).toISOString();
      }
      let expectedDate = null;
      if (poParsed.expected_delivery) {
        const parts = poParsed.expected_delivery.split('/');
        if (parts.length === 3) expectedDate = new Date(parts[2], parts[1] - 1, parts[0]).toISOString();
      }

      await api.post('/orders/purchase', {
        supplier: poParsed.supplier || 'Fournisseur inconnu',
        order_date: orderDate,
        expected_date: expectedDate,
        items,
        notes: `Import OCR - ${poParsed.order_number || 'Sans numero'}`,
      });

      toast.success('Bon de commande cree');
      setShowPOImportDialog(false);
      setPOParsed(null);
      fetchData();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur creation commande'));
    }
  };

  // Create product from unmatched PO item
  const handlePOCreateProduct = async (item, idx) => {
    setPOCreatingProduct(idx);
    try {
      const res = await productsApi.create({
        reference: item.reference,
        designation: item.designation || item.reference,
        category: item.category || 'F4',
        risk_class: item.risk_class || null,
        un_code: item.un_code || null,
        nec: item.nec || 0,
        gross_weight: item.gross_weight || 0,
        caliber: item.caliber || null,
        homologation_number: item.homologation_number || null,
        manufacturer: item.manufacturer || poParsed?.supplier || null,
      });
      const newProduct = res.data;
      
      // Move item from unmatched to matched
      const matchedItem = {
        ...item,
        product_id: newProduct.id,
        matched: true,
      };
      const newMatched = [...poParsed.matched_items, matchedItem];
      const newUnmatched = poParsed.unmatched_items.filter((_, i) => i !== idx);
      setPOParsed({
        ...poParsed,
        matched_items: newMatched,
        unmatched_items: newUnmatched,
        total_matched: newMatched.length,
        total_unmatched: newUnmatched.length,
      });
      // Refresh products list
      const prodRes = await productsApi.getAll();
      setProducts(prodRes.data?.items || prodRes.data || []);
      toast.success(`Produit "${item.reference}" cree dans la base articles`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur creation produit'));
    }
    setPOCreatingProduct(null);
  };

  // Associate unmatched PO item with existing product
  const handlePOAssociateProduct = (productId, item, idx) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    
    const matchedItem = {
      ...item,
      product_id: product.id,
      reference: product.reference,
      designation: product.designation,
      matched: true,
    };
    const newMatched = [...poParsed.matched_items, matchedItem];
    const newUnmatched = poParsed.unmatched_items.filter((_, i) => i !== idx);
    setPOParsed({
      ...poParsed,
      matched_items: newMatched,
      unmatched_items: newUnmatched,
      total_matched: newMatched.length,
      total_unmatched: newUnmatched.length,
    });
    setPOAssociating(null);
    setPOAssociateSearch('');
    toast.success(`"${item.reference}" associe a "${product.reference}"`);
  };


  const handleCreateReception = async (e) => {
    e.preventDefault();
    
    if (!receptionForm.depot_id) {
      toast.error('Sélectionnez un dépôt');
      return;
    }

    try {
      const data = {
        order_id: receptionForm.order_id,
        depot_id: receptionForm.depot_id,
        notes: receptionForm.notes || null,
        received_items: receptionForm.received_items.map(i => ({
          product_id: i.product_id,
          quantity: i.quantity,
          lot_number: i.lot_number || null,
          boxes: i.boxes || 1,
          location_id: i.location_id || null,
        })),
      };

      await receptionsApi.create(data);
      toast.success('Réception enregistrée');
      setShowReceptionDialog(false);
      setSelectedOrder(null);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Erreur lors de la réception'));
    }
  };

  const updateReceivedItem = (index, field, value) => {
    const newItems = [...receptionForm.received_items];
    newItems[index] = { ...newItems[index], [field]: value };
    setReceptionForm({ ...receptionForm, received_items: newItems });
  };

  const getOrderStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      partial: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      received: 'bg-green-500/20 text-green-400 border-green-500/30',
      cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
    };
    const labels = {
      pending: 'En attente',
      partial: 'Partiel',
      received: 'Réceptionné',
      cancelled: 'Annulé',
    };
    return (
      <Badge variant="outline" className={styles[status] || styles.pending}>
        {labels[status] || status}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" data-testid="receptions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">Réceptions</h1>
          <p className="text-zinc-400 text-sm mt-1">Gestion des commandes et réceptions marchandises</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowUploadDialog(true)}
            className="bg-cyan-500 hover:bg-cyan-400 text-black"
            data-testid="upload-delivery-note-button"
          >
            <Upload className="w-4 h-4 mr-2" />
            Importer BL
          </Button>
          <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="create-order-button">
              <Plus className="w-4 h-4 mr-2" />
              Nouvelle Commande
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">Nouveau Bon de Commande</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateOrder} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Fournisseur *</Label>
                  <Input
                    value={orderForm.supplier}
                    onChange={(e) => setOrderForm({...orderForm, supplier: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white"
                    placeholder="Nom du fournisseur"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Date de commande</Label>
                  <Input
                    type="date"
                    value={orderForm.order_date}
                    onChange={(e) => setOrderForm({...orderForm, order_date: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Date de livraison prévue</Label>
                  <Input
                    type="date"
                    value={orderForm.expected_date}
                    onChange={(e) => setOrderForm({...orderForm, expected_date: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Notes</Label>
                  <Input
                    value={orderForm.notes}
                    onChange={(e) => setOrderForm({...orderForm, notes: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white"
                    placeholder="Instructions particulières..."
                  />
                </div>
              </div>

              {/* Add Products */}
              <div className="space-y-4 p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                <Label className="text-zinc-300">Produits à commander</Label>
                <div className="flex gap-2">
                  <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                    <SelectTrigger className="flex-1 bg-zinc-900 border-zinc-700 text-white">
                      <SelectValue placeholder="Sélectionner un produit" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[200px]">
                      {products.map(product => (
                        <SelectItem key={product.id} value={product.id} className="text-white">
                          <span className="font-mono text-cyan-400">{product.reference}</span> - {product.designation?.substring(0, 25)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    value={selectedQuantity}
                    onChange={(e) => setSelectedQuantity(e.target.value)}
                    className="w-20 bg-zinc-900 border-zinc-700 text-white"
                    placeholder="Qté"
                    min="1"
                  />
                  <Input
                    type="number"
                    step="0.01"
                    value={selectedUnitPrice}
                    onChange={(e) => setSelectedUnitPrice(e.target.value)}
                    className="w-24 bg-zinc-900 border-zinc-700 text-white"
                    placeholder="Prix €"
                  />
                  <Button type="button" onClick={handleAddOrderItem} variant="outline" className="border-zinc-700">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {orderForm.items.length > 0 && (
                  <div className="space-y-2 mt-4">
                    {orderForm.items.map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-zinc-900 rounded border border-zinc-800">
                        <div className="flex-1">
                          <span className="font-mono text-cyan-400">{item.product_reference}</span>
                          <span className="text-zinc-400 ml-2">{item.product_designation?.substring(0, 25)}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-mono text-white">{item.quantity} pcs</span>
                          {item.unit_price > 0 && (
                            <span className="text-zinc-400">{item.unit_price.toFixed(2)} €</span>
                          )}
                          <Button 
                            type="button" 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleRemoveOrderItem(index)}
                            className="text-red-400 hover:text-red-300 h-6 w-6 p-0"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
                <Button type="button" variant="outline" onClick={() => setShowOrderDialog(false)} className="border-zinc-700">
                  Annuler
                </Button>
                <Button type="submit" className="bg-amber-500 hover:bg-amber-400 text-black">
                  Créer la commande
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Upload Delivery Note Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={(open) => { setShowUploadDialog(open); if (!open) { setParsedDelivery(null); setUploadDepotId(''); } }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-6xl w-[95vw] max-h-[90vh] overflow-y-auto"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Scan className="w-5 h-5 text-cyan-400" />
              Importer un Bon de Livraison
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Upload zone */}
            {!parsedDelivery && (
              <div
                ref={setDropZoneRef}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragOver ? 'border-cyan-400 bg-cyan-500/10' : 'border-zinc-700'}`}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  className="hidden"
                  data-testid="delivery-note-file-input"
                />
                {uploading ? (
                  <div className="space-y-3">
                    <Loader2 className="w-12 h-12 text-cyan-400 mx-auto animate-spin" />
                    <p className="text-white font-semibold">Analyse en cours...</p>
                    <p className="text-zinc-500 text-sm">L'IA interprète le bon de livraison</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Upload className="w-12 h-12 text-zinc-600 mx-auto" />
                    <p className="text-white font-semibold">Glissez ou sélectionnez votre bon de livraison</p>
                    <p className="text-zinc-500 text-sm">PDF, JPG, PNG ou WEBP — max 20 Mo</p>
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      className="bg-cyan-500 hover:bg-cyan-400 text-black"
                      data-testid="select-file-button"
                    >
                      <Upload className="w-4 h-4 mr-2" /> Sélectionner un fichier
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Parsed results */}
            {parsedDelivery && (
              <div className="space-y-6">
                {/* Delivery info header */}
                <div className="grid grid-cols-3 gap-4">
                  {parsedDelivery.supplier && (
                    <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800">
                      <p className="text-xs text-zinc-500">Fournisseur</p>
                      <p className="text-white font-semibold">{parsedDelivery.supplier}</p>
                    </div>
                  )}
                  {parsedDelivery.delivery_number && (
                    <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800">
                      <p className="text-xs text-zinc-500">N° Bon de Livraison</p>
                      <p className="font-mono text-cyan-400">{parsedDelivery.delivery_number}</p>
                    </div>
                  )}
                  {parsedDelivery.delivery_date && (
                    <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800">
                      <p className="text-xs text-zinc-500">Date</p>
                      <p className="text-white">{parsedDelivery.delivery_date}</p>
                    </div>
                  )}
                </div>

                {/* Summary */}
                <div className="flex items-center gap-4">
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-sm px-3 py-1">
                    <Check className="w-4 h-4 mr-1" /> {parsedDelivery.total_matched} reconnus
                  </Badge>
                  {parsedDelivery.total_unmatched > 0 && (
                    <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-sm px-3 py-1">
                      <XCircle className="w-4 h-4 mr-1" /> {parsedDelivery.total_unmatched} non reconnus
                    </Badge>
                  )}
                </div>

                {/* Matched products */}
                {parsedDelivery.matched_items.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-green-400 mb-3">Produits reconnus — En attente de validation</h4>
                    <div className="rounded-lg border border-zinc-800 overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-zinc-800 hover:bg-transparent">
                            <TableHead className="text-zinc-400 text-xs">Référence</TableHead>
                            <TableHead className="text-zinc-400 text-xs">Désignation</TableHead>
                            <TableHead className="text-zinc-400 text-xs">ADR</TableHead>
                            <TableHead className="text-zinc-400 text-xs">Type</TableHead>
                            <TableHead className="text-zinc-400 text-xs text-right">Quantité</TableHead>
                            <TableHead className="text-zinc-400 w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {parsedDelivery.matched_items.map((item, idx) => (
                            <TableRow key={idx} className="border-zinc-800" data-testid={`parsed-item-${idx}`}>
                              <TableCell className="font-mono text-cyan-400 text-xs whitespace-nowrap">{item.reference}</TableCell>
                              <TableCell className="text-white text-xs">{item.designation}</TableCell>
                              <TableCell>
                                {item.risk_class && (
                                  <Badge variant="outline" className="text-xs">{item.risk_class}</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-zinc-400 text-xs">
                                {item.unit_type === 'cartons' ? (
                                  <span className="text-amber-400">{item.original_quantity} crt → {item.quantity} pcs</span>
                                ) : 'unités'}
                              </TableCell>
                              <TableCell className="text-right">
                                <Input
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => handleUpdateParsedQty(idx, e.target.value)}
                                  className="w-20 h-8 bg-zinc-950 border-zinc-700 text-white text-right font-mono ml-auto"
                                  min="0"
                                />
                              </TableCell>
                              <TableCell>
                                <Button variant="ghost" size="sm" className="text-red-400 h-8 w-8 p-0"
                                  onClick={() => handleRemoveParsedItem(idx)}>
                                  <X className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Unmatched products */}
                {parsedDelivery.unmatched_items.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-red-400 mb-3">Produits non reconnus</h4>
                    <div className="space-y-2">
                      {parsedDelivery.unmatched_items.map((item, idx) => (
                        <div key={idx} className="p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                          <div className="flex items-start gap-3 flex-wrap">
                            <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                            <span className="font-mono text-red-400 text-sm">{item.reference}</span>
                            <span className="text-zinc-400 text-sm flex-1 min-w-0 break-words">{item.designation}</span>
                            <span className="text-zinc-500 text-sm whitespace-nowrap">x{item.quantity} {item.unit_type}</span>
                            <Button
                              size="sm"
                              className="bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 whitespace-nowrap flex-shrink-0"
                              onClick={() => handleOpenCreateFromUnmatched(item, idx)}
                              data-testid={`create-unmatched-${idx}`}
                            >
                              <Plus className="w-3 h-3 mr-1" /> Créer le produit
                            </Button>
                          </div>
                          {/* Show OCR-extracted extra info */}
                          {(item.homologation_number || item.risk_class || item.un_code || item.nec || item.manufacturer) && (
                            <div className="flex flex-wrap gap-2 mt-2 pl-7">
                              {item.homologation_number && <Badge variant="outline" className="text-[10px] text-cyan-400 border-cyan-500/30">Homol: {item.homologation_number}</Badge>}
                              {item.risk_class && <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/30">{item.risk_class}</Badge>}
                              {item.category && <Badge variant="outline" className="text-[10px] text-zinc-400 border-zinc-600">{item.category}</Badge>}
                              {item.un_code && <Badge variant="outline" className="text-[10px] text-zinc-400 border-zinc-600">{item.un_code}</Badge>}
                              {item.nec && <Badge variant="outline" className="text-[10px] text-amber-400/70 border-amber-500/20">NEC: {Math.round(item.nec * 1000 * 100) / 100} g</Badge>}
                              {item.manufacturer && <Badge variant="outline" className="text-[10px] text-zinc-500 border-zinc-700">{item.manufacturer}</Badge>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Depot allocation zone */}
                <div className="rounded-xl border border-zinc-800 overflow-hidden">
                  <div className="px-5 py-3 bg-zinc-900/80 border-b border-zinc-800">
                    <h4 className="text-sm font-semibold text-white">Répartition par dépôt</h4>
                    <p className="text-zinc-500 text-xs mt-0.5">Affectez chaque produit à un ou plusieurs dépôts</p>
                  </div>

                  {/* Quick assign */}
                  <div className="px-4 py-3 bg-zinc-950 border-b border-zinc-800 flex items-center gap-3">
                    <span className="text-zinc-400 text-xs font-semibold whitespace-nowrap">Tout envoyer vers :</span>
                    <Select value={uploadDepotId} onValueChange={(v) => { setUploadDepotId(v); handleAssignAllToDepot(v); }}>
                      <SelectTrigger className="flex-1 h-8 bg-zinc-900 border-zinc-700 text-white text-xs" data-testid="quick-assign-depot">
                        <SelectValue placeholder="Choisir un dépôt pour tout affecter d'un coup" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {depots.map(d => <SelectItem key={d.id} value={d.id} className="text-white">{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Per-item depot allocation */}
                  <div className="max-h-[35vh] overflow-y-auto">
                    {parsedDelivery.matched_items.filter(i => i.quantity > 0).map((item, idx) => {
                      const allocated = getReceptionAllocated(item.product_id);
                      const remaining = item.quantity - allocated;
                      return (
                        <div key={idx} className={`px-4 py-3 border-b border-zinc-800/50 ${remaining > 0 ? 'bg-red-500/5' : ''}`}>
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <span className="font-mono text-cyan-400 text-xs">{item.reference}</span>
                            <span className="text-zinc-300 text-xs truncate flex-1 min-w-0">{item.designation}</span>
                            <span className="text-white font-mono text-sm font-semibold whitespace-nowrap">{item.quantity}</span>
                            <span className="text-zinc-500 text-xs">{item.unit_type}</span>
                            {remaining === 0 && <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px]">OK</Badge>}
                            {remaining > 0 && <Badge className="bg-red-500/20 text-red-400 border-red-500/30 text-[10px]">Reste {remaining}</Badge>}
                            {remaining < 0 && <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px]">+{-remaining}</Badge>}
                          </div>
                          <div className="flex flex-wrap gap-1.5 pl-2">
                            {depots.map(d => {
                              const allocs = receptionDepots[item.product_id] || [];
                              const alloc = allocs.find(a => a.depot_id === d.id);
                              const qty = alloc?.qty || 0;
                              const isActive = qty > 0;
                              return (
                                <div key={d.id} className={`flex items-center gap-1 px-1.5 py-0.5 rounded border transition-colors ${
                                  isActive ? 'bg-amber-500/10 border-amber-500/30' : 'border-zinc-800 hover:border-zinc-600'
                                }`}>
                                  <span className={`text-[11px] truncate max-w-[80px] ${isActive ? 'text-amber-300' : 'text-zinc-500'}`} title={d.name}>{d.name}</span>
                                  <input type="number" min="0"
                                    value={qty || ''}
                                    placeholder="0"
                                    onChange={(e) => handleReceptionDepotQty(item.product_id, d.id, d.name, parseInt(e.target.value) || 0)}
                                    className={`w-14 h-6 rounded border px-1.5 text-xs text-center outline-none bg-zinc-950 ${
                                      isActive ? 'border-amber-500/40 text-amber-300' : 'border-zinc-700 text-zinc-400'
                                    } focus:border-amber-500/50`}
                                    data-testid={`reception-qty-${item.product_id}-${d.id}`}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" className="border-zinc-700"
                    onClick={() => { setParsedDelivery(null); setReceptionDepots({}); }}>
                    <Upload className="w-4 h-4 mr-2" /> Réanalyser un autre fichier
                  </Button>
                  <Button
                    onClick={handleValidateDelivery}
                    className="flex-1 bg-green-500 hover:bg-green-400 text-black font-semibold"
                    disabled={validating || parsedDelivery.matched_items.filter(i => i.quantity > 0).length === 0}
                    data-testid="validate-delivery-button"
                  >
                    {validating ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    )}
                    Valider et ajouter au stock ({parsedDelivery.matched_items.filter(i => i.quantity > 0).length} produits)
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="receptions" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <ClipboardCheck className="w-4 h-4 mr-2" /> Réceptions
          </TabsTrigger>
          <TabsTrigger value="orders" className="data-[state=active]:bg-amber-500 data-[state=active]:text-black">
            <FileText className="w-4 h-4 mr-2" /> Bons de Commande
          </TabsTrigger>
        </TabsList>

        {/* Receptions Tab */}
        <TabsContent value="receptions" className="mt-6">
          {/* Search & Sort Toolbar */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input value={recSearch} onChange={e => setRecSearch(e.target.value)}
                placeholder="Rechercher par ID, fournisseur, depot..."
                className="w-full bg-zinc-900 border border-zinc-800 rounded-lg pl-10 pr-4 py-2 text-sm text-white placeholder:text-zinc-600 focus:border-amber-500/50 focus:outline-none"
                data-testid="reception-search" />
            </div>
            <span className="text-zinc-600 text-xs">Tri:</span>
            <select value={recSortBy} onChange={e => setRecSortBy(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-2 text-xs text-zinc-300">
              <option value="received_at">Date</option>
              <option value="id">ID</option>
              <option value="supplier">Fournisseur</option>
            </select>
            <button onClick={() => setRecSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
              className="bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-2 text-xs text-zinc-300 hover:border-amber-500/50">
              {recSortOrder === 'asc' ? '↑ A-Z' : '↓ Z-A'}
            </button>
          </div>
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-400px)]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-400">ID Réception</TableHead>
                      <TableHead className="text-zinc-400">Fournisseur</TableHead>
                      <TableHead className="text-zinc-400">Date</TableHead>
                      <TableHead className="text-zinc-400">Dépôt</TableHead>
                      <TableHead className="text-zinc-400">Articles</TableHead>
                      <TableHead className="text-zinc-400">Statut</TableHead>
                      <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">
                          <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" />
                        </TableCell>
                      </TableRow>
                    ) : (() => {
                      let filtered = receptions;
                      if (recSearch) {
                        const q = recSearch.toLowerCase();
                        filtered = filtered.filter(r =>
                          r.id?.toLowerCase().includes(q) ||
                          (r.supplier || r.order_id || '').toLowerCase().includes(q) ||
                          (r.depot_name || '').toLowerCase().includes(q) ||
                          (r.notes || '').toLowerCase().includes(q)
                        );
                      }
                      filtered = [...filtered].sort((a, b) => {
                        let va = a[recSortBy] || '', vb = b[recSortBy] || '';
                        if (recSortBy === 'received_at') { va = new Date(va); vb = new Date(vb); }
                        if (va < vb) return recSortOrder === 'asc' ? -1 : 1;
                        if (va > vb) return recSortOrder === 'asc' ? 1 : -1;
                        return 0;
                      });
                      if (filtered.length === 0) return (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center py-8 text-zinc-500">
                            <Truck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>Aucune réception{recSearch ? ' trouvee' : ' enregistree'}</p>
                          </TableCell>
                        </TableRow>
                      );
                      return filtered.map((reception) => (
                        <TableRow key={reception.id} className="border-zinc-800 table-row-hover">
                          <TableCell className="font-mono text-cyan-400">{reception.id}</TableCell>
                          <TableCell className="font-mono text-zinc-400">{reception.supplier || reception.order_id}</TableCell>
                          <TableCell className="text-zinc-400">
                            {new Date(reception.received_at).toLocaleDateString('fr-FR')}
                          </TableCell>
                          <TableCell className="text-white">{reception.depot_name || depots.find(d => d.id === reception.depot_id)?.name || reception.depot_id?.slice(0, 12)}</TableCell>
                          <TableCell className="text-white">{reception.received_items?.length || 0}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/30">
                              <CheckCircle className="w-3 h-3 mr-1" /> Terminé
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button size="sm" variant="ghost" className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 h-8 w-8 p-0"
                                onClick={() => { setLabelItems((reception.received_items || []).map(i => ({ product_id: i.product_id, quantity: i.quantity }))); setShowLabelDialog(true); }}
                                data-testid={`labels-reception-${reception.id}`} title="Imprimer étiquettes">
                                <Printer className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                                onClick={() => handleDeleteReception(reception.id)}
                                data-testid={`delete-reception-${reception.id}`} title="Supprimer la réception">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Orders Tab */}
        <TabsContent value="orders" className="mt-6">
          {/* Import PO Button */}
          <div className="flex gap-2 mb-4">
            <Button onClick={() => setShowPOImportDialog(true)}
              className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="import-po-btn">
              <Upload className="w-4 h-4 mr-2" /> Importer Commande
            </Button>
          </div>

          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-400px)]">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800 hover:bg-transparent">
                      <TableHead className="text-zinc-400">N° Commande</TableHead>
                      <TableHead className="text-zinc-400">Fournisseur</TableHead>
                      <TableHead className="text-zinc-400">Date</TableHead>
                      <TableHead className="text-zinc-400">Livraison prévue</TableHead>
                      <TableHead className="text-zinc-400">Articles</TableHead>
                      <TableHead className="text-zinc-400">Statut</TableHead>
                      <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">
                          <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" />
                        </TableCell>
                      </TableRow>
                    ) : purchaseOrders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-zinc-500">
                          <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>Aucun bon de commande</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      purchaseOrders.map((order) => (
                        <TableRow key={order.id} className="border-zinc-800 table-row-hover">
                          <TableCell className="font-mono text-cyan-400">{order.id}</TableCell>
                          <TableCell className="text-white">{order.supplier}</TableCell>
                          <TableCell className="text-zinc-400">
                            {new Date(order.order_date).toLocaleDateString('fr-FR')}
                          </TableCell>
                          <TableCell className="text-zinc-400">
                            {order.expected_date ? new Date(order.expected_date).toLocaleDateString('fr-FR') : '-'}
                          </TableCell>
                          <TableCell className="text-white">{order.items?.length || 0}</TableCell>
                          <TableCell>{getOrderStatusBadge(order.status)}</TableCell>
                          <TableCell className="text-right">
                            {order.status === 'pending' && (
                              <Button
                                size="sm"
                                onClick={() => handleStartReception(order)}
                                className="bg-green-500/20 text-green-400 hover:bg-green-500/30"
                              >
                                <Truck className="w-4 h-4 mr-1" /> Réceptionner
                              </Button>
                            )}
                            {order.status === 'received' && (
                              <Button size="sm" variant="outline" className="border-zinc-700">
                                <Eye className="w-4 h-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Reception Dialog */}
      <Dialog open={showReceptionDialog} onOpenChange={setShowReceptionDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading">
              Réception de la commande {selectedOrder?.id}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateReception} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Dépôt de réception *</Label>
                <Select 
                  value={receptionForm.depot_id} 
                  onValueChange={(v) => setReceptionForm({...receptionForm, depot_id: v})}
                >
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white">
                    <SelectValue placeholder="Sélectionner un dépôt" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {depots.map(depot => (
                      <SelectItem key={depot.id} value={depot.id} className="text-white">
                        {depot.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Notes de réception</Label>
                <Input
                  value={receptionForm.notes}
                  onChange={(e) => setReceptionForm({...receptionForm, notes: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white"
                  placeholder="Observations..."
                />
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-zinc-300">Articles à réceptionner</Label>
              <div className="space-y-3">
                {receptionForm.received_items.map((item, index) => {
                  const product = products.find(p => p.id === item.product_id);
                  return (
                    <div key={index} className="p-4 bg-zinc-950 rounded-lg border border-zinc-800">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <span className="font-mono text-cyan-400">{product?.reference}</span>
                          <span className="text-zinc-400 ml-2">{product?.designation?.substring(0, 30)}</span>
                        </div>
                        <Badge variant="outline" className="text-white">
                          Commandé: {item.quantity}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-4 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs text-zinc-500">Qté reçue</Label>
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateReceivedItem(index, 'quantity', parseInt(e.target.value))}
                            className="bg-zinc-900 border-zinc-700 text-white"
                            min="0"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-zinc-500">N° Lot</Label>
                          <Input
                            value={item.lot_number}
                            onChange={(e) => updateReceivedItem(index, 'lot_number', e.target.value)}
                            className="bg-zinc-900 border-zinc-700 text-white font-mono"
                            placeholder="LOT-XXXX"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-zinc-500">Nb cartons</Label>
                          <Input
                            type="number"
                            value={item.boxes}
                            onChange={(e) => updateReceivedItem(index, 'boxes', parseInt(e.target.value))}
                            className="bg-zinc-900 border-zinc-700 text-white"
                            min="1"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-zinc-500">Emplacement</Label>
                          <Input
                            value={item.location_id}
                            onChange={(e) => updateReceivedItem(index, 'location_id', e.target.value)}
                            className="bg-zinc-900 border-zinc-700 text-white font-mono"
                            placeholder="A-01-02"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
              <Button type="button" variant="outline" onClick={() => setShowReceptionDialog(false)} className="border-zinc-700">
                Annuler
              </Button>
              <Button type="submit" className="bg-green-500 hover:bg-green-400 text-black">
                <CheckCircle className="w-4 h-4 mr-2" /> Valider la réception
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Label Print Dialog */}
      <LabelPrintDialog
        open={showLabelDialog}
        onOpenChange={setShowLabelDialog}
        items={labelItems}
        products={products}
      />

      {/* Create Product from Unmatched Dialog */}
      <Dialog open={!!showCreateFromUnmatched} onOpenChange={(open) => { if (!open) { setShowCreateFromUnmatched(null); setNewProductForm({}); } }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Plus className="w-5 h-5 text-amber-400" />
              Créer un nouveau produit
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30 text-sm text-zinc-400">
              Ce produit n'existe pas dans la base. Renseignez les informations pour le créer et l'ajouter à la réception.
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Référence *</Label>
                <Input value={newProductForm.reference || ''} onChange={(e) => setNewProductForm({...newProductForm, reference: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white font-mono" data-testid="new-product-reference" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Désignation *</Label>
                <Input value={newProductForm.designation || ''} onChange={(e) => setNewProductForm({...newProductForm, designation: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white" data-testid="new-product-designation" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Catégorie</Label>
                <Select value={newProductForm.category || ''} onValueChange={(v) => setNewProductForm({...newProductForm, category: v})}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {['F1','F2','F3','F4','T1','T2','P1','P2'].map(c => <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Classe ADR</Label>
                <Select value={newProductForm.risk_class || ''} onValueChange={(v) => setNewProductForm({...newProductForm, risk_class: v})}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {['1.1G','1.2G','1.3G','1.4G','1.4S'].map(c => <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Code ONU</Label>
                <Input value={newProductForm.un_code || ''} onChange={(e) => setNewProductForm({...newProductForm, un_code: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white font-mono" placeholder="Code ONU" />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">NEC (g)</Label>
                <Input type="number" step="0.01" value={newProductForm.nec || ''} onChange={(e) => setNewProductForm({...newProductForm, nec: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white font-mono" placeholder="NEC en grammes" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Calibre (mm)</Label>
                <Input value={newProductForm.caliber || ''} onChange={(e) => setNewProductForm({...newProductForm, caliber: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white" placeholder="Calibre" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Poids brut (g)</Label>
                <Input type="number" step="0.01" value={newProductForm.gross_weight || ''} onChange={(e) => setNewProductForm({...newProductForm, gross_weight: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white" placeholder="Poids brut en grammes" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Fabricant</Label>
                <Input value={newProductForm.manufacturer || ''} onChange={(e) => setNewProductForm({...newProductForm, manufacturer: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white" placeholder="Fabricant" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">N° Homologation</Label>
              <Input value={newProductForm.homologation_number || ''} onChange={(e) => setNewProductForm({...newProductForm, homologation_number: e.target.value})}
                className="bg-zinc-950 border-zinc-800 text-white font-mono" placeholder="N° d'agrément" />
            </div>

            <div className="p-3 bg-zinc-950 rounded-lg border border-zinc-800">
              <p className="text-xs text-zinc-500 mb-1">Quantité à réceptionner</p>
              <p className="text-lg font-mono text-white">{newProductForm.quantity || 0} {newProductForm.unit_type}</p>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-zinc-800">
              <Button variant="outline" className="border-zinc-700" onClick={() => { setShowCreateFromUnmatched(null); setNewProductForm({}); }}>
                Annuler
              </Button>
              <Button onClick={handleCreateAndAddProduct} className="bg-amber-500 hover:bg-amber-400 text-black" data-testid="confirm-create-product-button">
                <Plus className="w-4 h-4 mr-2" /> Créer et ajouter à la réception
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PO Import Dialog */}
      <Dialog open={showPOImportDialog} onOpenChange={(open) => { setShowPOImportDialog(open); if (!open) setPOParsed(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-5xl max-h-[90vh] overflow-y-auto"
          onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Upload className="w-5 h-5 text-amber-400" />
              Importer un Bon de Commande
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {!poParsed && (
              <div ref={setPODropZoneRef}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${poDragOver ? 'border-amber-400 bg-amber-500/10' : poImporting ? 'border-amber-500/50 bg-amber-500/5' : 'border-zinc-700 hover:border-zinc-500'}`}
                onClick={() => !poImporting && poFileInputRef.current?.click()}>
                <input ref={poFileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
                  onChange={(e) => handlePOFileUpload(e.target.files?.[0])} />
                {poImporting ? (
                  <>
                    <Loader2 className="w-10 h-10 mx-auto mb-3 text-amber-400 animate-spin" />
                    <p className="text-amber-400">Analyse du bon de commande en cours...</p>
                    <p className="text-zinc-600 text-sm mt-1">Extraction des references et quantites via IA</p>
                  </>
                ) : poDragOver ? (
                  <>
                    <Upload className="w-10 h-10 mx-auto mb-3 text-amber-400" />
                    <p className="text-amber-400 font-medium">Deposez le fichier ici</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-10 h-10 mx-auto mb-3 text-zinc-500" />
                    <p className="text-zinc-300">Glissez-deposez ou cliquez pour selectionner</p>
                    <p className="text-zinc-600 text-sm mt-1">PDF, JPG, PNG ou WEBP</p>
                  </>
                )}
              </div>
            )}

            {poParsed && (
              <>
                {/* Order info */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="bg-zinc-950 p-3 rounded-lg border border-zinc-800">
                    <p className="text-zinc-500 text-xs">Fournisseur</p>
                    <p className="text-white text-sm font-medium truncate">{poParsed.supplier || '-'}</p>
                  </div>
                  <div className="bg-zinc-950 p-3 rounded-lg border border-zinc-800">
                    <p className="text-zinc-500 text-xs">N° Commande</p>
                    <p className="text-cyan-400 font-mono text-sm">{poParsed.order_number || '-'}</p>
                  </div>
                  <div className="bg-zinc-950 p-3 rounded-lg border border-zinc-800">
                    <p className="text-zinc-500 text-xs">Date commande</p>
                    <p className="text-white text-sm">{poParsed.order_date || '-'}</p>
                  </div>
                  <div className="bg-zinc-950 p-3 rounded-lg border border-zinc-800">
                    <p className="text-zinc-500 text-xs">Livraison prevue</p>
                    <p className="text-amber-400 text-sm font-medium">{poParsed.expected_delivery || 'Non precisee'}</p>
                  </div>
                </div>

                {/* Matched items */}
                {poParsed.matched_items.length > 0 && (
                  <div>
                    <p className="text-green-400 text-sm font-medium mb-2 flex items-center gap-2">
                      <Check className="w-4 h-4" /> {poParsed.total_matched} produit(s) reconnu(s)
                    </p>
                    <div className="rounded-lg border border-zinc-800 overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-zinc-800 hover:bg-transparent">
                            <TableHead className="text-zinc-400 text-xs">Reference</TableHead>
                            <TableHead className="text-zinc-400 text-xs">Designation</TableHead>
                            <TableHead className="text-zinc-400 text-xs text-right">Quantite</TableHead>
                            <TableHead className="text-zinc-400 text-xs text-right">Prix unit.</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {poParsed.matched_items.map((item, idx) => (
                            <TableRow key={idx} className="border-zinc-800/50">
                              <TableCell className="font-mono text-cyan-400 text-xs">{item.reference}</TableCell>
                              <TableCell className="text-white text-xs">{item.designation}</TableCell>
                              <TableCell className="text-right font-mono text-white text-sm">{item.quantity}</TableCell>
                              <TableCell className="text-right font-mono text-amber-400 text-xs">
                                {item.unit_price ? `${item.unit_price.toFixed(2)} €` : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Unmatched items */}
                {poParsed.unmatched_items.length > 0 && (
                  <div>
                    <p className="text-red-400 text-sm font-medium mb-2 flex items-center gap-2">
                      <XCircle className="w-4 h-4" /> {poParsed.total_unmatched} produit(s) non reconnu(s)
                    </p>
                    <div className="space-y-1.5">
                      {poParsed.unmatched_items.map((item, idx) => (
                        <div key={idx} className="p-2.5 bg-red-500/10 rounded border border-red-500/20">
                          <div className="flex items-center gap-3 text-xs">
                            <XCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                            <span className="font-mono text-red-400">{item.reference}</span>
                            <span className="text-zinc-400 flex-1 truncate">{item.designation}</span>
                            <span className="text-zinc-500">x{item.quantity}</span>
                          </div>
                          <div className="flex gap-2 mt-2 ml-6">
                            <Button size="sm" variant="outline"
                              className="text-xs h-7 border-green-500/40 text-green-400 hover:bg-green-500/10"
                              disabled={poCreatingProduct === idx}
                              onClick={() => handlePOCreateProduct(item, idx)}
                              data-testid={`po-create-product-${idx}`}>
                              {poCreatingProduct === idx ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
                              Creer le produit
                            </Button>
                            <Button size="sm" variant="outline"
                              className="text-xs h-7 border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
                              onClick={() => { setPOAssociating(idx); setPOAssociateSearch(''); }}
                              data-testid={`po-associate-product-${idx}`}>
                              <Search className="w-3 h-3 mr-1" /> Associer a un existant
                            </Button>
                          </div>
                          {/* Product search dropdown for association */}
                          {poAssociating === idx && (
                            <div className="mt-2 ml-6 p-2 bg-zinc-950 rounded border border-zinc-700">
                              <Input
                                autoFocus
                                placeholder="Chercher par reference ou designation..."
                                value={poAssociateSearch}
                                onChange={(e) => setPOAssociateSearch(e.target.value)}
                                className="h-7 text-xs bg-zinc-900 border-zinc-700 text-white mb-2"
                                data-testid={`po-search-product-${idx}`}
                              />
                              <div className="max-h-32 overflow-y-auto space-y-0.5">
                                {products
                                  .filter(p => {
                                    if (!poAssociateSearch) return true;
                                    const s = poAssociateSearch.toLowerCase();
                                    return (p.reference || '').toLowerCase().includes(s) || (p.designation || '').toLowerCase().includes(s);
                                  })
                                  .slice(0, 10)
                                  .map(p => (
                                    <button key={p.id}
                                      className="w-full text-left px-2 py-1 text-xs rounded hover:bg-zinc-800 flex items-center gap-2"
                                      onClick={() => handlePOAssociateProduct(p.id, item, idx)}>
                                      <span className="font-mono text-cyan-400">{p.reference}</span>
                                      <span className="text-zinc-400 truncate">{p.designation}</span>
                                    </button>
                                  ))}
                                {products.filter(p => {
                                  if (!poAssociateSearch) return true;
                                  const s = poAssociateSearch.toLowerCase();
                                  return (p.reference || '').toLowerCase().includes(s) || (p.designation || '').toLowerCase().includes(s);
                                }).length === 0 && (
                                  <p className="text-zinc-500 text-xs text-center py-2">Aucun produit trouve</p>
                                )}
                              </div>
                              <Button size="sm" variant="ghost" className="text-xs h-6 text-zinc-500 w-full mt-1"
                                onClick={() => setPOAssociating(null)}>Annuler</Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-2 pt-2">
                  <Button variant="outline" onClick={() => setPOParsed(null)} className="flex-1 border-zinc-700 text-zinc-300">
                    Relancer l'analyse
                  </Button>
                  <Button onClick={handleCreateOrderFromPO}
                    disabled={poParsed.matched_items.length === 0}
                    className="flex-1 bg-green-600 hover:bg-green-500 text-white" data-testid="confirm-create-po">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Creer le bon ({poParsed.matched_items.length} art.)
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReceptionsPage;
